import { useState } from 'react';

import {
  AkaVeranstaltungId,
  type PlanungsinformationFromThemaRouter,
  type ThemaFromThemaRouter,
  type Zeitraum,
} from '../../../dtos';
import { isPcCenterThema } from '../../../utils';
import {
  type CalendarEvent,
  CalendarExperteResource,
  CalendarGruppenraumResource,
  CalendarSeminarraumResource,
  CalendarStandortResource,
  createCalendarResources,
  FilterParameters,
} from '../ResourceCalendar';
import {
  AbstractCalendarResource,
  SuggestedEvents,
} from '../ResourceCalendar/helperClasses/CalendarResource/AbstractCalendarResource.ts';
import { addValidationErrors } from '../ResourceCalendar/helperClasses/eventUtils.ts';
import { useExperten, useLocationsWithRaeume, useStandorte } from './useFilteredData.ts';

type UseResourcesAndEventsProps = {
  themaId: ThemaFromThemaRouter['id'];
  planungsinformation: PlanungsinformationFromThemaRouter;
  skipTrainerGueltigkeitsCheck: boolean;
  akaVeranstaltungId: AkaVeranstaltungId | null;
  filterParams: FilterParameters;
  timeRange: Zeitraum;
  view: 'week' | 'day';
  suggestedEvents?: SuggestedEvents;
};

export function useResourcesAndEvents({
  themaId,
  planungsinformation,
  skipTrainerGueltigkeitsCheck,
  akaVeranstaltungId,
  filterParams,
  timeRange,
  view,
  suggestedEvents,
}: UseResourcesAndEventsProps) {
  const [pendingEvents, setPendingEvents] = useState<readonly CalendarEvent[]>([]);
  const [fixedEvents, setFixedEvents] = useState<readonly CalendarEvent[]>([]);
  const [seminarraumResources, setSeminarraumResources] = useState<CalendarSeminarraumResource[]>([]);
  const [gruppenraumResources, setGruppenraumResources] = useState<CalendarGruppenraumResource[]>([]);
  const [standortResources, setStandortResources] = useState<CalendarStandortResource[]>([]);
  const [experteResources, setExperteResources] = useState<CalendarExperteResource[]>([]);

  const [lastUpdate, setLastUpdate] = useState(0);

  const clearState = (): void => {
    setPendingEvents([]);
    setLastUpdate(0);
  };

  const istTerminreihe = Boolean(planungsinformation.istTerminreihe);
  const locationsWithRaeume = useLocationsWithRaeume({
    istTerminreihe,
    filterParams,
    timeRange,
  });
  const standorte = useStandorte({ istTerminreihe, timeRange, standortSapIds: filterParams.standortSapIds });
  const expertePool = useExperten({ istTerminreihe, timeRange, themaId });

  const dataIsAvailable = locationsWithRaeume.isSuccess && standorte.isSuccess && expertePool.isSuccess;
  const dataIsNewer = [locationsWithRaeume.dataUpdatedAt, standorte.dataUpdatedAt, expertePool.dataUpdatedAt].some(
    (updatedAt) => updatedAt > lastUpdate,
  );

  // Update resources and fixed events on every refetch
  if (dataIsAvailable && dataIsNewer) {
    const { seminarraumResources, gruppenraumResources, standortResources, experteResources } = createCalendarResources(
      skipTrainerGueltigkeitsCheck,
      expertePool.data,
      locationsWithRaeume.data,
      standorte.data,
      timeRange.start,
      planungsinformation,
    );
    const resources = [...seminarraumResources, ...gruppenraumResources, ...standortResources, ...experteResources];

    setSeminarraumResources(seminarraumResources);
    setGruppenraumResources(gruppenraumResources);
    setStandortResources(standortResources);
    setExperteResources(experteResources);

    const newFixedEvents = createFixedEvents(resources, akaVeranstaltungId, view);
    setFixedEvents(newFixedEvents);

    // Only initialize pending events once
    const pendingEventsAvailable = Boolean(suggestedEvents) || Boolean(akaVeranstaltungId);
    if (lastUpdate === 0 && pendingEventsAvailable) {
      const newPendingEvents = createPendingEvents(resources, suggestedEvents, akaVeranstaltungId, view);

      addValidationErrors(
        newPendingEvents,
        newFixedEvents,
        Boolean(planungsinformation.istTerminreihe),
        isPcCenterThema(planungsinformation),
        seminarraumResources,
        experteResources,
      );

      setPendingEvents(newPendingEvents);
    }

    setLastUpdate(Math.max(locationsWithRaeume.dataUpdatedAt, standorte.dataUpdatedAt, expertePool.dataUpdatedAt));
  }

  return {
    seminarraumResources,
    gruppenraumResources,
    standortResources,
    experteResources,
    fixedEvents,
    pendingEvents,
    setPendingEvents,
    clearState,
  };
}

function createFixedEvents(
  resources: AbstractCalendarResource[],
  akaVeranstaltungId: AkaVeranstaltungId | null,
  view: 'week' | 'day',
) {
  return resources.flatMap((resource) =>
    resource.createFixedEventsFromBlockungen(akaVeranstaltungId).map((event) => {
      event.allDay = view === 'week';
      return event;
    }),
  );
}

export function createPendingEvents(
  resources: AbstractCalendarResource[],
  suggestedEvents: SuggestedEvents | undefined,
  akaVeranstaltungId: AkaVeranstaltungId | null,
  view: 'week' | 'day',
): CalendarEvent[] {
  return resources.flatMap((resource) => {
    if (suggestedEvents) {
      return resource.createPendingEventsFromSuggestions(suggestedEvents, akaVeranstaltungId).map((event) => {
        event.allDay = view === 'week';
        return event;
      });
    } else if (akaVeranstaltungId) {
      return resource.createPendingEventsFromBlockungen(akaVeranstaltungId);
    } else {
      throw new Error('suggestedEvents or akaVeranstaltungId must be set');
    }
  });
}
